/**
 * docxMarker.ts
 * Placeholder-based DOCX template parser for Mini Printer.
 *
 * Templates now use docxtemplater-native `{{…}}` placeholders instead of the
 * old `label⟦FIELD⟧` marker scheme:
 *
 *   Số PO:          {{Số PO}}
 *   Nhà cung cấp:   {{Nhà cung cấp}}
 *
 * The field name is the text inside the braces. Repeating detail tables are
 * delimited with docxtemplater loop syntax:
 *
 *   {{#items}}  …  {{/items}}
 *
 * and contain a single sample row whose cells carry `{{fieldName}}` tags.
 *
 * Control tokens that are NOT data fields keep their `⟦…⟧` form so docxtemplater
 * ignores them (it would otherwise try to render `{{PAGE}}` / `{{SUM:x}}` as
 * data keys):
 *   ⟦PAGE⟧ / ⟦TOTAL⟧  — footer page counters (replaced at export/render time)
 *   ⟦SUM:fieldName⟧   — column total (replaced during fill)
 */

import { normalizeHeader } from "./columnMatcher";

/** Footer page-number markers (replaced at export/print time). */
export const PAGE_MARKER = "⟦PAGE⟧";
export const TOTAL_MARKER = "⟦TOTAL⟧";
/** Word-style aliases for the same counters: {{PAGE}} / {{NUMPAGES}}. */
export const PAGE_VAR = "{{PAGE}}";
export const NUMPAGES_VAR = "{{NUMPAGES}}";
/** Column-total marker: ⟦SUM:fieldName⟧ or {{SUM:fieldName}}. */
export const SUM_RE = /(?:⟦SUM:([^⟧]*)⟧|\{\{SUM:([^}]*)\}\})/g;

/**
 * Number-to-words marker (accounting / invoices): ⟦TEXT:fieldName⟧ turns a
 * field's value into Vietnamese words, e.g. ⟦TEXT:Tổng tiền⟧ → "một triệu ...".
 * ⟦TEXT:SUM:fieldName⟧ spells out the column total instead.
 * Also accepts the {{TEXT:...}} canonical form.
 */
export const TEXT_RE = /(?:⟦TEXT:([^⟧]*)⟧|\{\{TEXT:([^}]*)\}\})/g;

/**
 * @deprecated Legacy ⟦FIELD⟧ marker. Kept only so old call-sites still compile;
 * the parser no longer recognizes it. Do NOT use in new templates.
 */
export const FIELD_MARKER = "⟦FIELD⟧";
/** @deprecated Legacy repeat markers — no longer used; see {{#items}}…{{/items}}. */
export const REPEAT_START = "⟦REPEAT:START⟧";
export const REPEAT_END = "⟦REPEAT:END⟧";
/** @deprecated Legacy conditional markers — removed from the flow. */
export const IF_END = "⟦/IF⟧";
export const IF_RE = /⟦IF:([^⟧]*)⟧/g;

/** A single `{{…}}` token found in document.xml. */
interface PlaceholderToken {
  kind: "field" | "loop_start" | "loop_end";
  /** For field: the field name. For loops: the loop variable name. */
  name: string;
  /** Absolute offset of the token in document.xml. */
  offset: number;
  /** Offset right after the token. */
  end: number;
  raw: string;
}

/** Clean a raw label string (kept for backward-compat call-sites). */
export function cleanFieldLabel(raw: string): string {
  if (!raw) return "";
  return String(raw)
    .replace(/\u00A0/g, " ")
    .replace(/[\t\r\n]+/g, " ")
    .replace(/[:：]+\s*$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Normalize `[[…]]` placeholders to the canonical `{{…}}` form so the whole
 * pipeline (parser, fill, preview) only ever sees one delimiter set. This lets
 * templates written with `[[Tên]]` / `[[#items]]…[[/items]]` work identically
 * to `{{Tên}}` / `{{#items}}…{{/items}}`.
 *
 * Only `[[` / `]]` pairs are rewritten; existing `{{…}}` are left untouched.
 */
export function normalizeBracketDelimiters(docXml: string): string {
  if (!docXml.includes("[[") && !docXml.includes("]]")) return docXml;
  return docXml.replace(/\[\[/g, "{{").replace(/\]\]/g, "}}");
}

/** Normalize a field name for comparison with Excel headers. */
export const normalizeFieldName = normalizeHeader;

function textOf(xml: string): string {
  const matches = xml.match(/<w:t[^>]*>([\s\S]*?)<\/w:t>/g) ?? [];
  return matches
    .map((t) => t.replace(/<w:t[^>]*>/, "").replace(/<\/w:t>$/, ""))
    .join("");
}

function escapeXmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Distinct token shapes that Word may split across adjacent `<w:t>` runs after
 * inserting a logo. `{{…}}` placeholders and the `⟦…⟧` control tokens are all
 * repaired the same way: if a paragraph's text contains a token the XML does
 * not hold contiguously, the whole paragraph text is merged into its first run.
 */
function isSplittableToken(plain: string): boolean {
  return /{{/.test(plain) || /⟦[^⟧]*⟧/.test(plain);
}

/**
 * Word often splits `{{Mã CH}}` / `⟦SUM:...⟧` across adjacent runs:
 *   <w:t>{{</w:t> … <w:t>Mã CH</w:t> … <w:t>}}</w:t>
 * Those are invisible to string search and stay on the printed page as raw
 * placeholders. This merges them back into a single contiguous token inside
 * the first `<w:t>`.
 */
export function repairSplitMarkers(docXml: string): { xml: string; repairedCount: number } {
  let repairedCount = 0;

  const xml = docXml.replace(/<w:p\b[\s\S]*?<\/w:p>/gi, (paraXml) => {
    const plain = textOf(paraXml);
    if (!isSplittableToken(plain)) return paraXml;

    // Determine whether any token is split: the plain text has a token the
    // raw XML does not contain contiguously.
    const tags = plain.match(/\{\{[^{}]*\}\}/g) ?? [];
    const controls = plain.match(/⟦[^⟧]*⟧/g) ?? [];
    const needs = [...tags, ...controls].some((t) => !paraXml.includes(t));
    if (!needs) return paraXml;

    let first = true;
    const next = paraXml.replace(/<w:t(\s[^>]*)?>[\s\S]*?<\/w:t>/g, (_tTag, attrs = "") => {
      if (!first) return `<w:t${attrs}></w:t>`;
      first = false;
      const hasSpace = /\bxml:space=/.test(attrs);
      const attr = hasSpace ? attrs : `${attrs} xml:space="preserve"`;
      return `<w:t${attr}>${escapeXmlText(plain)}</w:t>`;
    });

    if (next !== paraXml) repairedCount += 1;
    return next;
  });

  return { xml, repairedCount };
}

/** True when `xml` at `index` starts a real Word paragraph. */
function isParagraphOpen(xml: string, index: number): boolean {
  if (!xml.startsWith("<w:p", index)) return false;
  const next = xml.charAt(index + 4);
  return (
    next === ">" || next === " " || next === "\t" || next === "\n" || next === "\r"
  );
}

function isTableRowOpen(xml: string, index: number): boolean {
  if (!xml.startsWith("<w:tr", index)) return false;
  const next = xml.charAt(index + 5);
  return next === ">" || next === " " || next === "\t" || next === "\n" || next === "\r";
}

/** Return the enclosing <w:p> element for a given character offset. */
export function enclosingPara(xml: string, offset: number): string {
  let start = -1;
  let from = offset;
  while (from >= 0) {
    const i = xml.lastIndexOf("<w:p", from);
    if (i === -1) break;
    if (isParagraphOpen(xml, i)) {
      start = i;
      break;
    }
    from = i - 1;
  }
  if (start === -1) return "";

  let depth = 0;
  let pos = start;
  while (pos < xml.length) {
    const nextOpen = xml.indexOf("<w:p", pos);
    const nextClose = xml.indexOf("</w:p>", pos);
    if (nextClose === -1) return xml.slice(start);
    if (nextOpen !== -1 && nextOpen < nextClose && isParagraphOpen(xml, nextOpen)) {
      depth++;
      pos = nextOpen + 4;
      continue;
    }
    depth--;
    if (depth === 0) return xml.slice(start, nextClose + "</w:p>".length);
    pos = nextClose + "</w:p>".length;
  }
  return xml.slice(start);
}

/** Return the enclosing <w:tr> element (table row) for a given offset, if any. */
function enclosingTr(xml: string, offset: number): string {
  let start = -1;
  let from = offset;
  while (from >= 0) {
    const i = xml.lastIndexOf("<w:tr", from);
    if (i === -1) break;
    if (isTableRowOpen(xml, i)) {
      start = i;
      break;
    }
    from = i - 1;
  }
  if (start === -1) return "";
  const end = xml.indexOf("</w:tr>", offset);
  return xml.slice(start, end === -1 ? xml.length : end + "</w:tr>".length);
}

/** A single field placeholder found in the DOCX template. */
export interface DocxMarkerField {
  /** Field name taken verbatim from inside the `{{…}}` tag (e.g. "Số PO"). */
  fieldName: string;
  /** Normalized field name (diacritics/case/space-insensitive) for matching. */
  normalizedName: string;
  /** The raw placeholder token, e.g. "{{Số PO}}". */
  marker: string;
  /** True when this placeholder sits inside a `{{#…}} … {{/…}}` loop. */
  inRepeat: boolean;
  /** Always false — ⟦IF⟧ blocks are no longer supported. */
  inCondition: boolean;
  /** Raw XML of the enclosing <w:p>. */
  paragraphXml: string;
  /** Offset of the placeholder inside word/document.xml. */
  markerOffset: number;
  /** Mapping result (populated by the matcher). */
  status?: "mapped" | "not_found" | "ambiguous";
  excelColumn?: string;
}

/** A `{{#name}} … {{/name}}` repeat region. */
export interface RepeatRange {
  startOffset: number;
  endOffset: number;
  /** Field names detected inside the repeat region. */
  fieldNames: string[];
  /** Raw XML of the first <w:tr> inside the region (the sample row). */
  sampleRowXml: string;
  /** True if no sample <w:tr> was found inside the region. */
  missingSampleRow: boolean;
}

/** Result of parsing a DOCX template for placeholders. */
export interface DocxMarkerParseResult {
  fields: DocxMarkerField[];
  repeatRanges: RepeatRange[];
  /** Human-readable validation errors. */
  errors: string[];
  warnings: string[];
  /** True when the template contains at least one `{{field}}` placeholder. */
  hasFieldMarkers: boolean;
}

/**
 * Scan document.xml for every `{{…}}` token and classify it as a field, loop
 * start (`{{#name}}`) or loop end (`{{/name}}`). `⟦SUM:…⟧` / `⟦PAGE⟧` /
 * `⟦TOTAL⟧` are control tokens and are ignored here.
 */
function scanPlaceholders(docXml: string): PlaceholderToken[] {
  const tokens: PlaceholderToken[] = [];
  const re = /\{\{([^{}]*)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(docXml)) !== null) {
    const inner = m[1];
    const offset = m.index;
    const end = m.index + m[0].length;
    if (inner.startsWith("#")) {
      tokens.push({ kind: "loop_start", name: inner.slice(1).trim(), offset, end, raw: m[0] });
    } else if (inner.startsWith("/")) {
      tokens.push({ kind: "loop_end", name: inner.slice(1).trim(), offset, end, raw: m[0] });
    } else {
      const upper = inner.toUpperCase().trim();
      // Control tokens: page counters, column totals and number-to-words are
      // not data fields.
      if (upper === "PAGE" || upper === "TOTAL" || upper === "NUMPAGES") continue;
      if (upper.startsWith("SUM:")) continue;
      if (upper.startsWith("TEXT:")) continue;
      tokens.push({ kind: "field", name: inner.trim(), offset, end, raw: m[0] });
    }
  }
  return tokens;
}

/**
 * Parse a DOCX document.xml string and extract all field placeholders, loop
 * ranges and validation issues.
 */
export function extractDocxMarkers(docXml: string): DocxMarkerParseResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  const tokens = scanPlaceholders(docXml);
  const loopStarts = tokens.filter((t) => t.kind === "loop_start");
  const loopEnds = tokens.filter((t) => t.kind === "loop_end");
  const fieldTokens = tokens.filter((t) => t.kind === "field");

  if (loopStarts.length !== loopEnds.length) {
    errors.push(
      `Vùng repeat không đầy đủ marker bắt đầu hoặc kết thúc (${loopStarts.length} {{#…}} / ${loopEnds.length} {{/…}}).`
    );
  }

  // Pair loop starts/ends by name (order of appearance within each name).
  const pairs: { start: PlaceholderToken; end: PlaceholderToken }[] = [];
  const byName = new Map<string, PlaceholderToken[]>();
  for (const t of loopStarts) {
    const arr = byName.get(t.name) ?? [];
    arr.push(t);
    byName.set(t.name, arr);
  }
  const consumed = new Set<string>();
  for (const name of byName.keys()) {
    const starts = byName.get(name)!;
    const ends = loopEnds.filter((e) => e.name === name);
    for (let i = 0; i < Math.min(starts.length, ends.length); i++) {
      if (ends[i].offset <= starts[i].offset) {
        errors.push(`Vùng repeat "${name}" có marker đóng đứng trước marker mở.`);
        continue;
      }
      pairs.push({ start: starts[i], end: ends[i] });
      consumed.add(`${name}:${starts[i].offset}:${ends[i].offset}`);
    }
  }

  const repeatRanges: RepeatRange[] = pairs.map(({ start, end }) => {
    const fieldInRegion = fieldTokens.filter(
      (f) => f.offset >= start.end && f.end <= end.offset
    );
    const fieldNames = fieldInRegion.map((f) => f.name);
    const sampleRowXml = enclosingTr(docXml, start.offset);
    const missingSampleRow = fieldInRegion.length === 0;
    if (missingSampleRow) {
      errors.push(`Bảng lặp "{{#${start.name}}}" không có hàng mẫu để nhân bản.`);
    }
    return {
      startOffset: start.offset,
      endOffset: end.offset,
      fieldNames: Array.from(new Set(fieldNames)),
      sampleRowXml,
      missingSampleRow,
    };
  });

  // Legacy ⟦IF⟧ warning (kept because a stale template may still carry it).
  if (IF_RE.test(docXml) || docXml.includes(IF_END)) {
    warnings.push(
      "Template chứa ⟦IF⟧ cũ không còn được hỗ trợ — hãy gỡ khỏi template và dùng \"Điều kiện hiển thị trang\" thay thế."
    );
  }
  IF_RE.lastIndex = 0;

  const fields: DocxMarkerField[] = [];
  const seenNames = new Map<string, number>();
  for (const t of fieldTokens) {
    const inRepeat = repeatRanges.some(
      (r) => t.offset > r.startOffset && t.offset < r.endOffset
    );
    const fieldName = t.name;
    if (!fieldName) {
      errors.push("Có placeholder rỗng {{}} — hãy điền tên trường bên trong.");
      continue;
    }
    seenNames.set(fieldName, (seenNames.get(fieldName) ?? 0) + 1);
    fields.push({
      fieldName,
      normalizedName: normalizeFieldName(fieldName),
      marker: t.raw,
      inRepeat,
      inCondition: false,
      paragraphXml: enclosingPara(docXml, t.offset),
      markerOffset: t.offset,
    });
  }

  for (const [name, cnt] of seenNames) {
    if (cnt > 1) {
      warnings.push(`Tên trường "${name}" xuất hiện ${cnt} lần trong template.`);
    }
  }

  if (fieldTokens.length === 0) {
    errors.push(
      "Template không chứa placeholder {{tên}}. Không thể xác định vị trí điền dữ liệu."
    );
  }

  return {
      fields,
      repeatRanges,
      errors,
      warnings,
      hasFieldMarkers: fieldTokens.length > 0,
    };
  }

  // ────────────────────────────────────────────────────────────
  // Legacy ⟦IF:…⟧ … ⟦/IF⟧ support (DEPRECATED)
  //
  // Kept only so stale templates and old tests still compile. The app no longer
  // uses ⟦IF⟧ in templates (page visibility is user-configured in the UI), and
  // extractDocxMarkers just warns when a template carries it. These functions
  // operate on the raw ⟦IF⟧ tokens and are not part of the placeholder flow.
  // ────────────────────────────────────────────────────────────

  /** Parse the ⟦IF:...⟧ token into { column, value } (value may be empty = any non-empty). */
  export function parseIfToken(token: string): { column: string; value: string } | null {
    const m = token.match(/^⟦IF:([^⟧]*)⟧$/s);
    if (!m) return null;
    const body = m[1]?.trim() ?? "";
    const eq = body.indexOf("=");
    if (eq === -1) return { column: body.trim(), value: "" };
    return { column: body.slice(0, eq).trim(), value: body.slice(eq + 1).trim() };
  }

  /** A ⟦IF:...⟧ … ⟦/IF⟧ block with token offsets. */
  export interface IfBlock {
    startOffset: number;
    /** Offset right after the ⟦IF:...⟧ token. */
    startTokenEnd: number;
    endOffset: number;
    /** Offset right after the ⟦/IF⟧ token. */
    endTokenEnd: number;
    column: string;
    value: string;
  }

  /**
   * Find all ⟦IF:...⟧ … ⟦/IF⟧ blocks by scanning tokens in document order.
   * Nesting is not supported and is reported as a structural error rather than
   * silently mis-paired.
   */
  export function findIfBlocks(docXml: string): { blocks: IfBlock[]; errors: string[] } {
    const errors: string[] = [];

    interface Token {
      kind: "start" | "end";
      offset: number;
      end: number;
      column: string;
      value: string;
    }
    const tokens: Token[] = [];

    const startRe = new RegExp(IF_RE.source, "g");
    let m: RegExpExecArray | null;
    while ((m = startRe.exec(docXml)) !== null) {
      const parsed = parseIfToken(m[0]);
      tokens.push({
        kind: "start",
        offset: m.index,
        end: m.index + m[0].length,
        column: parsed?.column ?? "",
        value: parsed?.value ?? "",
      });
    }
    const endRe = /⟦\/IF⟧/g;
    let m2: RegExpExecArray | null;
    while ((m2 = endRe.exec(docXml)) !== null) {
      tokens.push({
        kind: "end",
        offset: m2.index,
        end: m2.index + m2[0].length,
        column: "",
        value: "",
      });
    }
    tokens.sort((a, b) => a.offset - b.offset);

    const startCount = tokens.filter((tk) => tk.kind === "start").length;
    const endCount = tokens.length - startCount;
    if (startCount !== endCount) {
      errors.push(
        `Vùng điều kiện ⟦IF⟧ không cân bằng (${startCount} mở / ${endCount} đóng).`
      );
    }

    const blocks: IfBlock[] = [];
    let open: Token | null = null;
    let nestingReported = false;
    let strayEndReported = false;

    for (const tk of tokens) {
      if (tk.kind === "start") {
        if (open) {
          if (!nestingReported) {
            errors.push("Vùng điều kiện ⟦IF⟧ lồng nhau không được hỗ trợ.");
            nestingReported = true;
          }
          continue;
        }
        open = tk;
        continue;
      }
      if (!open) {
        if (!strayEndReported) {
          errors.push("Có ký hiệu ⟦/IF⟧ đứng trước ⟦IF:...⟧.");
          strayEndReported = true;
        }
        continue;
      }
      blocks.push({
        startOffset: open.offset,
        startTokenEnd: open.end,
        endOffset: tk.offset,
        endTokenEnd: tk.end,
        column: open.column,
        value: open.value,
      });
      open = null;
    }

    return { blocks, errors };
  }